import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  AddCircle,
  Build,
  BugReport,
  Star,
} from '@mui/icons-material';

interface ChangelogEntry {
  version: string;
  date: string;
  added: string[];
  changed: string[];
  fixed: string[];
}

const userChangelogs: ChangelogEntry[] = [
  {
    version: '1.9.14',
    date: '2026-10-08',
    added: [],
    changed: [
      'Application Android : compatibilité Android 16 (niveau d\'API 36), exigée par Google Play.',
    ],
    fixed: [],
  },
  {
    version: '1.9.13',
    date: '2026-07-04',
    added: [
      'Ajout en masse (app) : recherche joueur par joueur ou collage d\'emails.',
    ],
    changed: [
      'Import email : détection automatique des comptes PadPal ; les autres reçoivent une invitation.',
    ],
    fixed: [],
  },
  {
    version: '1.9.12',
    date: '2026-07-04',
    added: [
      'Ajout en masse de participants (app + API) : coller une liste d\'emails ; les utilisateurs PadPal sont ajoutés directement, les autres reçoivent une invitation.',
    ],
    changed: [],
    fixed: [],
  },
  {
    version: '1.9.11',
    date: '2026-07-04',
    added: [],
    changed: [
      'Page tournoi publique : mise en page mobile (cartes match, classement compact, plus de défilement horizontal).',
    ],
    fixed: [
      'Saisie du score (app) : les deux joueurs de chaque paire s\'affichent correctement.',
    ],
  },
  {
    version: '1.9.10',
    date: '2026-07-04',
    added: [
      'App : assignation des terrains à la création ou modification d\'un tournoi Mexicano/Americano (un terrain par 4 joueurs).',
    ],
    changed: [],
    fixed: [
      'Saisie du score Mexicano/Americano (app) : plus d\'erreur « le match doit être terminé » sur les matchs confirmés.',
      'Nouvelle API pour enregistrer le score des formats rotatifs sans ligne TournamentMatch.',
    ],
  },
  {
    version: '1.9.9',
    date: '2026-07-04',
    added: [],
    changed: [
      'Page publique Mexicano : onglet Classement, terrains par manche, colonne Terrain, paires affichées correctement.',
      'Affichage du score (web + app) : sets gagnés (2-0) au lieu du total de jeux (12-4).',
    ],
    fixed: [
      'Classement Mexicano/Americano recalculé après saisie du score (points et victoires).',
      'API matchs tournoi : joueurs, avatars, terrain et détail des sets pour les formats rotatifs.',
    ],
  },
  {
    version: '1.9.8',
    date: '2026-07-03',
    added: [
      'Tournoi Mexicano : lancement des manches par classement (1+4 vs 2+3), points par jeux gagnés, bouton Lancer la manche.',
      'Tournoi Americano : rotation des partenaires à chaque manche, lancement des manches par l\'organisateur.',
    ],
    changed: [
      'Matchs groupés par manche (Mexicano, Americano, Montante) sur l\'app et la page publique.',
      'Création de tournoi (app) : libellés traduits ; nombre de manches optionnel.',
      'Classement web : colonne Points pour Mexicano/Americano ; onglet Joueurs.',
    ],
    fixed: [],
  },
  {
    version: '1.9.7',
    date: '2026-06-12',
    added: [],
    changed: [
      'Page tournoi (web) : scores par set (6-4, 6-2) et avatars d\'équipe colorés à la place des cercles gris.',
      'Admin web : saisie du score sur 2 sets par défaut ; 3e set seulement si égalité 1-1.',
      'Organisateur (app) : saisie du score set par set sur les matchs de tournoi.',
    ],
    fixed: [
      'Synchronisation du match tournoi après saisie admin du score (vainqueur et classement).',
      'Matchs terminés avec score confirmé mais sans vainqueur sur le tableau : comptés au classement après sync.',
    ],
  },
  {
    version: '1.9.6',
    date: '2026-06-12',
    added: [
      'Script de réparation des tournois (données corrompues, ex. tournoi 4).',
    ],
    changed: [
      'Organisateur / admin : saisie du score qui valide et termine le match directement.',
      'Classement : seuls les matchs terminés avec un vainqueur sont comptés.',
    ],
    fixed: [
      'Onglet Classement : erreur serveur (500) sur certains tournois.',
      'Résultats incorrects après un match « terminé » sans score ni vainqueur.',
      'Organisateur : bouton « Saisir le score » sur les matchs du tournoi (app).',
      'Impossible de clôturer un match tournoi sans score valide.',
    ],
  },
  {
    version: '1.9.5',
    date: '2026-05-30',
    added: [],
    changed: [],
    fixed: [
      'Bouton « Créer un match » actif pour l\'organisateur (sélection Équipe 1 + Équipe 2).',
      'Créneau sans match jouable : proposition de recréer le match avec les équipes pré-remplies.',
      'Actualisation de la liste avant ouverture d\'un match (réparation côté serveur).',
    ],
  },
  {
    version: '1.9.4',
    date: '2026-05-30',
    added: [
      'Admin : gestion complète depuis la fiche match (équipes, score, valider, supprimer) — app et web.',
      'Tournoi : reconnexion automatique des matchs jouables aux créneaux du tableau.',
      'Organisateur / admin : créer un match entre deux équipes depuis l\'onglet Matchs (app).',
    ],
    changed: [
      'Création de match tournoi : rattache le créneau « à planifier » au match jouable (plus de doublon confirmé / planifié).',
      'Liste des matchs du tournoi : une seule ligne par rencontre.',
      'Organisateur : sélection des deux équipes sans être joueur du match.',
    ],
    fixed: [
      'Plus de message « match non lié » après suppression du doublon par l\'admin.',
      'Suppression admin : le créneau tournoi repasse en « à planifier » au lieu d\'un lien cassé.',
    ],
  },
  {
    version: '1.9.3',
    date: '2026-05-30',
    added: [
      'Admin : gérer un match depuis sa fiche (équipes, score, valider, supprimer) — app et web.',
      'App : panneau admin sur l\'écran match ; appui sur un emplacement du terrain pour assigner un joueur.',
    ],
    changed: [
      'Liste admin des matchs : ouvre la fiche match standard.',
    ],
    fixed: [],
  },
  {
    version: '1.9.2',
    date: '2026-05-30',
    added: [
      'Réservation de 2 créneaux consécutifs = un seul match (tournoi par équipes et matchs classiques).',
      'Admin web : gestion des matchs (filtres, grille compacte, détail avec invités et scores).',
      'Admin app : liste compacte des matchs avec filtres.',
    ],
    changed: [
      'Création de match : pas de double envoi automatique ; messages d\'erreur API visibles.',
    ],
    fixed: [
      'Plus de doublons de matchs à la création avec 2 créneaux.',
      'Admin web : noms des participants invités, statuts, retrait de joueur.',
    ],
  },
  {
    version: '1.9.1',
    date: '2026-05-30',
    added: [
      'Admin : confirmation avant suppression d\'un tournoi ou retrait d\'une équipe.',
    ],
    changed: [
      'Retrait d\'équipe possible quel que soit le statut (en attente, approuvé, refusé).',
    ],
    fixed: [
      'Matchs de tournoi : noms des joueurs invités visibles dans les participants.',
      'Suppression tournoi / retrait équipe : cascade des matchs liés sans erreur.',
    ],
  },
  {
    version: '1.9.0',
    date: '2026-05-30',
    added: [
      'Page tournoi web : profils joueurs, onglet Équipes, API profil public.',
    ],
    changed: [
      'Libellés « équipes » pour les tournois par équipes ; nom d\'équipe mis en avant.',
    ],
    fixed: [
      'Scores des matchs terminés sur la page tournoi publique.',
      'Profils joueurs et édition des clubs participants (web + app, tous formats).',
    ],
  },
  {
    version: '1.8.9',
    date: '2026-05-30',
    added: [],
    changed: [
      'Onglet Équipes : nom d\'équipe en grand sur la page tournoi web.',
      'Tournois par équipes : libellé « équipes » au lieu de « participants ».',
    ],
    fixed: [
      'Scores affichés pour les matchs terminés sur la page tournoi publique.',
    ],
  },
  {
    version: '1.8.8',
    date: '2026-05-30',
    added: [
      'Onglet Équipes sur la page tournoi web avec noms de joueurs cliquables.',
    ],
    changed: [],
    fixed: [
      'Profils joueurs inscrits : chargement via API publique et repli sur les stats des participants.',
    ],
  },
  {
    version: '1.8.7',
    date: '2026-05-30',
    added: [
      'Profils joueurs sur la page tournoi web : clic sur un nom pour voir stats et ELO.',
      'Joueurs invités non inscrits : message « Pas encore inscrit ».',
    ],
    changed: [],
    fixed: [],
  },
  {
    version: '1.8.6',
    date: '2026-05-30',
    added: [],
    changed: [
      'Page tournoi web : français par défaut sur padpal.fr/t/{id}.',
    ],
    fixed: [
      'Ajout d\'équipes mixtes (joueur inscrit + invité) dans les deux ordres de créneaux.',
    ],
  },
  {
    version: '1.8.5',
    date: '2026-05-30',
    added: [
      'E-mail d\'invitation automatique lorsqu\'un organisateur ajoute un joueur par e-mail.',
      'Lien web de connexion (padpal.fr/login) pour revendiquer sa place après inscription.',
    ],
    changed: [
      'Liens d\'invitation : URL web uniquement, plus de lien application.',
    ],
    fixed: [],
  },
  {
    version: '1.8.4',
    date: '2026-05-30',
    added: [
      'Modification des équipes : les organisateurs peuvent changer le nom, la composition et retirer une équipe.',
      'Liens d\'invitation vers la connexion PadPal ; le joueur revendique sa place après inscription.',
    ],
    changed: [],
    fixed: [],
  },
  {
    version: '1.8.3',
    date: '2026-05-30',
    added: [
      'Invitations organisateur : ajoutez une équipe complète depuis l\'onglet Participants (joueurs inscrits ou invités par nom et e-mail).',
      'Liens d\'invitation à copier ou partager ; le joueur revendique sa place après inscription ou connexion.',
    ],
    changed: [],
    fixed: [
      'Correction d\'erreurs d\'affichage sur les écrans tournoi, match et score (identifiants manquants).',
    ],
  },
  {
    version: '1.8.2',
    date: '2026-05-31',
    added: [],
    changed: [
      'Onglet Matchs du tournoi : filtre « Tous » sélectionné par défaut.',
    ],
    fixed: [
      'Confirmation de score admin et validation tournoi : le match est bien terminé et le classement se met à jour.',
      'Correction du commit manquant après validation du score (match restait non terminé en base).',
    ],
  },
  {
    version: '1.8.1',
    date: '2026-05-30',
    added: [],
    changed: [],
    fixed: [
      'Création de match d\'équipe : détection correcte de votre équipe lors de la création d\'un match de tournoi.',
    ],
  },
  {
    version: '1.8.0',
    date: '2026-05-30',
    added: [
      'Création de matchs de tournoi par équipes (mobile) : votre équipe est pré-sélectionnée, choisissez l\'adversaire, réservez 2 créneaux consécutifs — les 4 joueurs sont ajoutés automatiquement.',
      'Scores de match d\'équipe : noms d\'équipes dans le détail du match ; soumission et validation via les endpoints tournoi pour un classement par équipe.',
    ],
    changed: [
      'Statistiques tournoi : les formats par équipes comptent une victoire/défaite par équipe, pas par joueur.',
    ],
    fixed: [
      'API user-matches : flux équipes unifié avec lien TournamentMatch et effectif complet.',
    ],
  },
  {
    version: '1.7.8',
    date: '2026-05-30',
    added: [],
    changed: [
      'Tournois ligue et formats par équipes : onglet Équipes et classements affichent le nom d\'équipe avec les deux avatars joueurs (mobile et page publique /t/:id).',
      'Flex League, Montante, Americano et Mexicano : affichage joueur individuel conservé.',
    ],
    fixed: [
      'API tournois : avatars joueurs inclus dans les réponses participants et classement.',
    ],
  },
  {
    version: '1.7.7',
    date: '2026-05-30',
    added: [],
    changed: [],
    fixed: [
      'Tournois : correction de l\'erreur serveur sur la liste des tournois (colonne max_teams et statut en base).',
    ],
  },
  {
    version: '1.7.6',
    date: '2026-05-30',
    added: [],
    changed: [
      'Migrations schéma automatiques au démarrage backend (colonnes manquantes).',
    ],
    fixed: [
      'Erreurs serveur sur les matchs : backend de production doit être compilé depuis la branche main (PadPal), pas depuis l\'ancien fork Padel-Camp.',
    ],
  },
  {
    version: '1.7.5',
    date: '2026-05-30',
    added: [],
    changed: [],
    fixed: [
      'Tournois ligue : affichage des équipes (2 joueurs) au lieu de joueurs solo dans les listes, classements et compteurs.',
      'Création tournoi : statut inscription par défaut.',
    ],
  },
  {
    version: '1.7.4',
    date: '2026-02-23',
    added: [
      'Réinitialisation mot de passe (web) : page publique /reset-password?token=xxx. Le lien dans l\'email ouvre un formulaire « Créer un nouveau mot de passe » au lieu de rediriger vers la connexion.',
    ],
    changed: [],
    fixed: [],
  },
  {
    version: '1.7.3',
    date: '2026-02-23',
    added: [],
    changed: [
      'Emails : suppression de tous les boutons « Ouvrir dans l\'app » dans les e-mails (EN/FR). Les boutons admin et réinitialisation mot de passe (web) restent.',
    ],
    fixed: [
      'Connexion mobile : les builds production utilisent toujours l\'API production (api.padpal.fr). Plus de « pas de réponse du serveur » si vous aviez passé en DEV.',
      'Logs d\'erreur API : code et message affichés en cas d\'absence de réponse.',
    ],
  },
  {
    version: '1.7.2',
    date: '2026-02-22',
    added: [
      'Tableau de bord admin – photos utilisateurs : avatars dans la liste, en-tête progression, menu profil.',
      'Tableau de bord admin – page détail utilisateur : /admin/users/:userId avec avatar, stats, actions Modifier et Voir la progression.',
      'Tableau de bord admin – liens matchs : les matchs récents de la progression mènent au détail du match.',
      'Admin i18n : page utilisateurs traduite (EN, FR).',
    ],
    changed: [
      'Menu Admin : Users reste mis en évidence sur les sous-routes ; Admin Panel sur /dashboard.',
      'Section journaux système marquée comme démo tant que l\'endpoint n\'existe pas.',
    ],
    fixed: [
      'Type User avec avatar_url ; helper frontend pour les URLs d\'avatar.',
    ],
  },
  {
    version: '1.7.1',
    date: '2026-02-22',
    added: [],
    changed: [
      'Mapping des niveaux (ELO 1-10 → niveaux Padel 1-8).',
      'Écran de chargement simplifié (logo uniquement).',
      'Matchs confirmés inclus dans le filtre À venir.',
    ],
    fixed: [
      'Requêtes N+1 dans les APIs matches, time_slots, statistics.',
      'Gestion des erreurs d\'authentification et traduction des messages.',
      'Nettoyage des tokens à la déconnexion.',
    ],
  },
  {
    version: '1.7.0',
    date: '2026-02-21',
    added: [
      'Système ELO avec fiabilité (facteur K).',
      'Couleurs de statut centralisées sur tous les écrans.',
      'Indicateur de fiabilité visuel dans les profils.',
      'Traductions pour l\'écran des matchs et les messages d\'erreur.',
    ],
    changed: [
      'Mapping des niveaux de compétence amélioré.',
      'Écran de chargement simplifié.',
      'Matchs confirmés inclus dans le filtre À venir.',
    ],
    fixed: [
      'Problèmes N+1 dans les APIs.',
      'Gestion des erreurs d\'authentification et traduction.',
      'Messages de connexion correctement traduits.',
    ],
  },
  {
    version: '1.6.9',
    date: '2026-02-17',
    added: [
      'Level-based partner chat: "Search for a partner by chat" from Profile opens channels by ELO (1–2, 3–4, 5–6, 7–8). Group chat per channel with send, edit (long-press), and delete. i18n EN, FR, ES, PT.',
      'Admin channel selector: Admins see a channel list and can access all five channels; regular users go directly to their first allowed channel.',
      'Feature flag can_access_level_chat: Access to level chat is gated. Admins always have access; others require the flag (enables moderators). Manage via Admin → Feature Flags.',
    ],
    changed: [
      'Profile "Matches played" stat now opens PlayedMatches screen (match history) on tap.',
      'Level chat channels updated to 12, 34, 56, 78 (Levels 1–2, 3–4, 5–6, 7–8).',
    ],
    fixed: [],
  },
  {
    version: '1.6.6',
    date: '2026-01-27',
    added: [
      'User progress page (web admin): From the users list, "View progress" opens a dedicated page per user with ELO (current/initial), total matches, win rate, streaks, matches-over-time chart, wins vs losses chart, and recent matches table. EN/FR translations.',
    ],
    changed: [],
    fixed: [
      'Frontend build: date-fns parseISO replaced with native Date for compatibility; MUI v7 Grid uses size prop instead of item on progress page.',
    ],
  },
  {
    version: '1.6.5',
    date: '2026-01-27',
    added: [
      'Played matches screen (mobile): Profile "Previously played matches" opens a dedicated screen listing completed matches, sorted by date, with court, date, time and score.',
      'Already booked match creation (mobile): Create Match supports "From available slots" and "Already booked". In Already booked mode you pick court and time manually. i18n EN, FR, ES, PT.',
    ],
    changed: [],
    fixed: [],
  },
  {
    version: '1.6.4',
    date: '2026-01-27',
    added: [
      'Past matches (mobile): New "Past" filter on Matches screen; calendar shows previous 30 days when Past is selected. i18n for Past, Load more, count (EN, FR, ES, PT).',
      'Friendly/private matches (backend): Matches auto-confirm when they reach 4 players (including 4th as guest). Match-confirmed notification and email to participants.',
    ],
    changed: [
      'Matches screen (mobile): Pagination fixed—page resets on filter/date/club change; "Load more" button with loading state; API params sent per filter.',
    ],
    fixed: [
      'Matches screen (mobile): Calendar and list no longer show wrong or empty data when changing pages or filters.',
    ],
  },
  {
    version: '1.6.3',
    date: '2026-01-27',
    added: [
      'Match chat notifications (mobile): Notifications from the same match are grouped (Android: single channel; iOS: thread). Opening chat from notification scrolls to last message.',
    ],
    changed: [
      'Competitive match (mobile): Level selection fixed to your level ±1 (1–8). Friendly/Private still use full sliders.',
      'Version bump to 1.6.3.',
    ],
    fixed: [
      'Create Match (mobile): Bottom navigation bar stays fixed and no longer scrolls with the form.',
    ],
  },
  {
    version: '1.6.2',
    date: '2026-01-27',
    added: [
      'SSO integration: "Open PadelSkill" in profile menu fetches a short-lived token and opens PadelSkill in a new tab for seamless auth.',
      'Backend: POST /auth/sso-token; SSO config (SSO_JWT_SECRET, etc.). K8s: optional SSO_JWT_SECRET; REACT_APP_PADELSKILL_URL in frontend build.',
    ],
    changed: [
      'Frontend: PadelSkill URL configurable via REACT_APP_PADELSKILL_URL (default https://padelskill.saffist3r.xyz).',
      'Deploy: Default IMAGE_TAG in deploy.sh updated to 1.6.2.',
    ],
    fixed: [],
  },
  {
    version: '1.6.1',
    date: '2026-01-27',
    added: [
      'Organisateurs de tournoi : les admins peuvent ajouter des organisateurs. Modal dédié sur mobile, section avec dialogue sur le web.',
      'Documentation : guide des organisateurs de tournoi.',
    ],
    changed: [
      'Lien de partage (mobile) : déplacé dans la section nom du tournoi – une icône partage, tap pour copier l\'URL.',
      'Lien de partage (web admin) : section dédiée avec URL et bouton Copier.',
      'Modal d\'édition de tournoi (mobile) : format, statut, format_config, clubs, sélection de court Montante.',
    ],
    fixed: [
      'Affichage des organisateurs : noms correctement affichés dans les chips.',
      'Créateur en double : exclu de la liste des organisateurs supplémentaires.',
    ],
  },
  {
    version: '1.6.0',
    date: '2026-01-27',
    added: [
      'Formats de tournoi : Montante/Descendante (échelle, pas d\'impact ELO), Americano (score individuel, partenaires rotatifs), Mexicano (matchs selon classement).',
      'Exclusion ELO : les tournois Montante/Descendante n\'affectent pas les classements ELO.',
      'i18n page publique tournoi : traductions EN/FR pour /t/:id. Statuts traduits.',
      'Couleurs de statut : chips pour statut tournoi (actif, inscription, à venir, terminé, annulé) et match.',
    ],
    changed: [
      'Page publique tournoi : redesign (thème sombre, logo, footer, hero) aligné avec padpal.fr.',
      'Échelle Montante (web) : colonne Points retirée, affichage W-L uniquement.',
    ],
    fixed: [
      'Scores sur le web : les matchs de tournoi réguliers affichent maintenant les scores confirmés.',
    ],
  },
  {
    version: '1.5.0',
    date: '2026-01-27',
    added: [
      'Langue préférée : synchronisation de la langue de l\'app vers le backend pour les notifications push traduites',
    ],
    changed: [
      'Suppression de match : correction des contraintes de clé étrangère (TournamentMatch, TournamentMatchCreation)',
      'Traduction des notifications : paramètres centralisés, fallback sur titre/corps stockés',
    ],
    fixed: [
      'Suppression de match : les admins peuvent à nouveau supprimer des matchs (FK nullifiées avant suppression)',
    ],
  },
  {
    version: '1.4.9',
    date: '2026-01-27',
    added: [],
    changed: [
      'Notifications d\'invitation : clés de traduction notifications.matchinvitation (en, fr, es, pt)',
    ],
    fixed: [
      'Chat : saisie et clavier correctement gérés sur Android',
      'Modales Invite et Ajouter joueur : clavier et défilement corrects sur Android',
    ],
  },
  {
    version: '1.4.8',
    date: '2026-01-27',
    added: [
      'Invitation de joueurs : invitez des membres du club ou recherchez des utilisateurs pour rejoindre vos matchs',
      'Modal InviteToMatch : sélection multiple, notifications push localisées',
    ],
    changed: [],
    fixed: [],
  },
  {
    version: '1.4.1',
    date: '2026-01-27',
    added: [],
    changed: [],
    fixed: [
      'Boucle infinie des notifications de chat : les notifications push ne se répètent plus indéfiniment',
    ],
  },
  {
    version: '1.4.0',
    date: '2026-01-27',
    added: [
      'Météo pour les terrains extérieurs : affichage de l\'aptitude au padel en extérieur (température, icônes selon les conditions)',
      'Indicateur terrain intérieur/extérieur dans l\'admin (mobile et web)',
      'Amélioration du chat de match : séparateurs de date (Aujourd\'hui, Hier), bulles modernisées, synchronisation plus rapide (2 s)',
    ],
    changed: [
      'Carte météo avec en-tête, bordure colorée et icônes par condition',
      'API messages : paramètre since_id pour un polling efficace',
    ],
    fixed: [
      'Sync Île des Loisirs : les terrains avec staff_id sont maintenant correctement synchronisés (plus d\'erreur "city et club_id requis")',
    ],
  },
  {
    version: '1.3.3',
    date: '2026-01-XX',
    added: [
      'Thème mode sombre avec palette de couleurs bleu-gris moderne',
      'Écran Aide & Support avec FAQ complète et email de contact',
      'Affichage du nom du court dans la sélection des créneaux pour la création de match',
      'Bouton toggle pour les filtres avec style moderne',
      'Bouton "Créer un match" intégré dans l\'en-tête des filtres',
    ],
    changed: [
      'Les filtres sont maintenant masqués par défaut sur l\'écran des matchs',
      'Styles modernisés pour les boutons de filtres avec conteneurs d\'icônes circulaires et ombres',
      'Amélioration de l\'espacement et de la mise en page dans l\'écran de création de match',
      'Formatage des dates et heures utilise maintenant la locale i18n pour une traduction correcte',
      'La vidéo du splash screen se termine maintenant avant la navigation',
    ],
    fixed: [
      'L\'upload d\'avatar navigue maintenant correctement vers le profil utilisateur au lieu du profil joueur',
      'La validation des tokens vérifie maintenant correctement avec l\'API backend au démarrage de l\'app',
      'L\'écran Create Match inclut maintenant une barre de navigation en bas',
      'Le fond de l\'écran de profil utilisateur se met à jour correctement avec les changements de thème',
      'Synchronisation du thème sur tous les écrans en utilisant ThemeContext',
      'Le toggle de notifications persiste maintenant correctement les préférences et s\'intègre avec le service',
      'Correction des problèmes d\'espace vide sous la barre de navigation dans l\'écran de création de match',
      'Correction de l\'erreur de traduction pour le bouton toggle des filtres',
    ],
  },
  {
    version: '1.3.2',
    date: '2026-01-12',
    added: [
      'Système de classement ELO avec fiabilité basée sur le facteur K',
      'Couleurs de statut centralisées sur tous les écrans',
      'Indicateur de fiabilité visuel dans les profils utilisateurs',
      'Traductions pour l\'écran des matchs et les messages d\'erreur',
      'Écran Outils de développement accessible depuis le tableau de bord Admin',
      'Basculement d\'environnement (DEV/PROD) avec configuration IP',
      'Outils de notification : envoyer à tous les utilisateurs, tester les notifications, voir l\'historique',
      'Affichage des informations de l\'application (version, build, environnement, etc.)',
      'Noms de jours traduits dans le calendrier de l\'écran des matchs (Anglais, Français, Portugais, Espagnol)',
    ],
    changed: [
      'Amélioration du mapping des niveaux de compétence (ELO 1-10 → Niveaux Padel 1-8)',
      'Écran de chargement simplifié (logo uniquement)',
      'Inclusion des matchs confirmés dans le filtre \'À venir\'',
      'Les créateurs de match rejoignent automatiquement avec leur côté préféré (gauche/droite)',
      'Outils de développement et de notification déplacés vers l\'écran Dev dédié',
      'Amélioration de l\'espacement des boutons dans le tableau de bord Admin pour une mise en page cohérente',
    ],
    fixed: [
      'Problèmes de requêtes N+1 dans les APIs matches, time_slots et statistics',
      'Gestion des erreurs d\'authentification et traduction des messages',
      'Nettoyage des tokens lors de la déconnexion',
      'Messages d\'erreur de connexion maintenant correctement traduits',
      'Espacement régulier entre les boutons dans le tableau de bord Admin',
      'L\'attribution de position du créateur de match respecte maintenant la préférence de l\'utilisateur',
    ],
  },
  {
    version: '1.3.1',
    date: '2026-01-03',
    added: [],
    changed: [
      'API endpoint updated to use api.padpal.fr for improved reliability',
    ],
    fixed: [
      'Fixed server connection errors by ensuring correct API endpoint configuration',
      'Improved API endpoint resolution for production environment',
    ],
  },
  {
    version: '1.3.0',
    date: '2026-01-02',
    added: [
      'Middleware de journalisation des requêtes API',
      'En-têtes email améliorés (Message-ID, Date, MIME-Version)',
      'Version texte des emails pour une meilleure délivrabilité',
      'Fonctionnalité de réinitialisation de mot de passe avec liens profonds',
    ],
    changed: [
      'URL de l\'API de production mise à jour vers api.padpal.fr',
      'La vidéo de démarrage ne s\'affiche plus après la création de compte',
      'Amélioration de la génération de texte à partir des templates HTML',
    ],
    fixed: [
      'Correction des problèmes de livraison des emails de bienvenue',
      'Correction de l\'affichage de la vidéo de démarrage après inscription',
      'Amélioration de la journalisation des requêtes API',
    ],
  },
  {
    version: '1.2.9',
    date: '2025-01-02',
    added: [
      'Écran de démarrage avec vidéo aléatoire et branding PadPal',
      'Nouveau système d\'avatar par défaut avec logo en niveaux de gris',
      'Filtres de tournois affichés par défaut',
      'Support complet des traductions pour le quiz ELO',
    ],
    changed: [
      'Refonte complète du système ELO avec distinction entre niveau initial et classement dynamique',
      'Interface de profil joueur modernisée avec hiérarchie d\'information améliorée',
      'Indicateur de fiabilité avec visualisation WiFi au lieu de "Variabilité"',
      'Affichage ELO sur échelle 8.0 au lieu de 10.0',
      'Couleurs du thème mises à jour avec palette cyan-bleu moderne',
      'Logo mis à jour avec nouveau design centré',
    ],
    fixed: [
      'Correction des problèmes d\'affichage des avatars',
      'Correction de l\'icône WiFi signal',
      'Correction des données de profil affichées',
      'Correction du chargement des images de profil et de club',
      'Correction de la boucle de rechargement sur l\'écran d\'accueil',
    ],
  },
  {
    version: '1.2.8',
    date: '2025-12-16',
    added: [
      'Nouvelle page calendrier pour les administrateurs',
      'Visualisation des créneaux horaires par semaine, jour et mois',
      'Filtrage par club et court',
      'Réservation et blocage interactifs des créneaux',
    ],
    changed: [
      'Amélioration de la gestion des erreurs sur toutes les pages',
      'Meilleure stabilité de l\'application',
    ],
    fixed: [
      'Correction des erreurs d\'affichage sur plusieurs pages',
      'Correction des problèmes de chargement des données',
    ],
  },
  {
    version: '1.2.7',
    date: '2025-12-12',
    added: [
      'Liens de paiement pour les tournois',
      'Sélection de clubs pour les tournois',
      'Filtrage des clubs lors de la création de matchs',
      'Traductions pour les statuts de match',
    ],
    changed: [
      'Redesign du message d\'approbation en attente',
      'Amélioration de l\'affichage des participants',
      'Seuls les participants approuvés apparaissent dans les classements',
    ],
    fixed: [
      'Correction de l\'affichage des liens de paiement',
      'Correction de l\'association tournoi-club',
    ],
  },
  {
    version: '1.2.6',
    date: '2025-12-05',
    added: [
      'Système de drapeaux de fonctionnalités',
      'Contrôle d\'accès pour la création et la visualisation de matchs',
      'Tableau de bord d\'administration pour les drapeaux de fonctionnalités',
    ],
    changed: [
      'Protection des endpoints de match avec vérification des drapeaux',
      'Interface utilisateur améliorée pour les fonctionnalités désactivées',
    ],
    fixed: [],
  },
  {
    version: '1.2.0',
    date: '2025-01-27',
    added: [
      'Types de matchs : compétitif, amical et privé',
      'Joueurs invités pour les matchs amicaux et privés',
      'Badges de type de match sur les cartes et écrans de détails',
    ],
    changed: [
      'Seuls les matchs compétitifs affectent le classement ELO',
      'Les matchs privés sont automatiquement filtrés des listes publiques',
    ],
    fixed: [],
  },
  {
    version: '1.1.0',
    date: '2025-11-25',
    added: [
      'Nouveau système de classement ELO',
      'Indicateur de fiabilité visuel',
      'Amélioration du mapping des niveaux de compétence',
      'Couleurs de statut cohérentes dans toute l\'application',
    ],
    changed: [
      'Écran de chargement simplifié',
      'Interface du profil modernisée',
      'Badges de statut avec couleurs distinctes',
    ],
    fixed: [
      'Optimisation des performances de chargement',
      'Correction des messages d\'erreur d\'authentification',
      'Amélioration de la gestion des tokens',
    ],
  },
  {
    version: '1.0.0',
    date: '2025-10-17',
    added: [
      'Gestion complète des tournois',
      'Gestion des utilisateurs et authentification',
      'Gestion des clubs et courts',
      'Système de réservation de créneaux',
      'Applications web et mobiles',
      'Notifications en temps réel',
      'Statistiques et classements',
      'Tableau de bord administrateur',
    ],
    changed: [],
    fixed: [],
  },
];

const ChangelogPage: React.FC = () => {
  const getIcon = (type: 'added' | 'changed' | 'fixed') => {
    switch (type) {
      case 'added':
        return <AddCircle sx={{ fontSize: 18 }} />;
      case 'changed':
        return <Build sx={{ fontSize: 18 }} />;
      case 'fixed':
        return <BugReport sx={{ fontSize: 18 }} />;
    }
  };

  const getColor = (type: 'added' | 'changed' | 'fixed') => {
    switch (type) {
      case 'added':
        return 'success';
      case 'changed':
        return 'primary';
      case 'fixed':
        return 'warning';
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#0a0a0a',
        color: 'white',
        overflow: 'hidden',
        position: 'relative',
        pt: 8,
        pb: 8,
      }}
    >
      {/* Abstract Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '-20%',
            left: '-10%',
            width: '50%',
            height: '50%',
            bgcolor: 'primary.main',
            opacity: 0.2,
            borderRadius: '50%',
            filter: 'blur(120px)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-20%',
            right: '-10%',
            width: '40%',
            height: '40%',
            bgcolor: '#2563eb',
            opacity: 0.1,
            borderRadius: '50%',
            filter: 'blur(100px)',
          }}
        />
      </Box>

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                letterSpacing: '-0.02em',
                mb: 2,
                background: 'linear-gradient(to bottom right, #fff, rgba(255, 255, 255, 0.5))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Journal des modifications
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '1.125rem',
              }}
            >
              Découvrez toutes les nouveautés et améliorations de PadPal
            </Typography>
          </Box>
        </motion.div>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {userChangelogs.map((entry, index) => (
            <motion.div
              key={entry.version}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1, ease: 'easeOut' }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: '1.5rem',
                  bgcolor: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(8px)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.06)',
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Chip
                    icon={<Star />}
                    label={`Version ${entry.version}`}
                    color="primary"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.875rem',
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontSize: '0.875rem',
                    }}
                  >
                    {entry.date}
                  </Typography>
                </Box>

                {entry.added.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <AddCircle sx={{ fontSize: 20, color: 'success.main' }} />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          fontSize: '1rem',
                          color: 'success.main',
                        }}
                      >
                        Nouveautés
                      </Typography>
                    </Box>
                    <Box component="ul" sx={{ m: 0, pl: 3, listStyle: 'none' }}>
                      {entry.added.map((item, i) => (
                        <Box
                          key={i}
                          component="li"
                          sx={{
                            mb: 1.5,
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '0.9375rem',
                            lineHeight: 1.6,
                            '&::before': {
                              content: '"•"',
                              color: 'success.main',
                              fontWeight: 'bold',
                              display: 'inline-block',
                              width: '1em',
                              marginLeft: '-1em',
                            },
                          }}
                        >
                          {item}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {entry.changed.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Build sx={{ fontSize: 20, color: 'primary.main' }} />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          fontSize: '1rem',
                          color: 'primary.main',
                        }}
                      >
                        Modifications
                      </Typography>
                    </Box>
                    <Box component="ul" sx={{ m: 0, pl: 3, listStyle: 'none' }}>
                      {entry.changed.map((item, i) => (
                        <Box
                          key={i}
                          component="li"
                          sx={{
                            mb: 1.5,
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '0.9375rem',
                            lineHeight: 1.6,
                            '&::before': {
                              content: '"•"',
                              color: 'primary.main',
                              fontWeight: 'bold',
                              display: 'inline-block',
                              width: '1em',
                              marginLeft: '-1em',
                            },
                          }}
                        >
                          {item}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {entry.fixed.length > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <BugReport sx={{ fontSize: 20, color: 'warning.main' }} />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          fontSize: '1rem',
                          color: 'warning.main',
                        }}
                      >
                        Corrections
                      </Typography>
                    </Box>
                    <Box component="ul" sx={{ m: 0, pl: 3, listStyle: 'none' }}>
                      {entry.fixed.map((item, i) => (
                        <Box
                          key={i}
                          component="li"
                          sx={{
                            mb: 1.5,
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '0.9375rem',
                            lineHeight: 1.6,
                            '&::before': {
                              content: '"•"',
                              color: 'warning.main',
                              fontWeight: 'bold',
                              display: 'inline-block',
                              width: '1em',
                              marginLeft: '-1em',
                            },
                          }}
                        >
                          {item}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            </motion.div>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default ChangelogPage;

